import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { learningAPI } from '../api/client';
import { ArrowLeft, Play, Clock, BookOpen, CheckCircle } from 'lucide-react';

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCourseData();
  }, [courseId]);

  const loadCourseData = async () => {
    try {
      const [courseResponse, lessonsResponse] = await Promise.all([
        learningAPI.getCourse(courseId),
        learningAPI.getCourseLessons(courseId)
      ]);
      
      setCourse(courseResponse.data);
      setLessons(lessonsResponse.data);
    } catch (error) {
      console.error('Kurs bilgileri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Kurs bulunamadı</p>
        <button
          onClick={() => navigate('/learning')}
          className="text-blue-600 hover:text-blue-700"
        >
          Kurslara Dön
        </button>
      </div>
    );
  }

  const totalDuration = lessons.reduce((sum, lesson) => sum + (lesson.duration || 0), 0);

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={() => navigate('/learning')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Kurslara Dön
      </button>

      {/* Course Header */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
        <div className="aspect-video bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="inline-block p-6 bg-white bg-opacity-20 rounded-full mb-4">
              <BookOpen size={64} />
            </div>
          </div>
        </div>
        
        <div className="p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-4 py-2 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
              {course.category}
            </span>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{course.title}</h1>
          <p className="text-xl text-gray-600 mb-6">{course.description}</p>
          
          <div className="flex items-center gap-6 text-gray-600">
            <div className="flex items-center gap-2">
              <Clock size={20} />
              <span>{Math.floor(totalDuration / 60)} saat {totalDuration % 60} dakika</span>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen size={20} />
              <span>{lessons.length} ders</span>
            </div>
          </div>
        </div>
      </div>

      {/* Lessons List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Kurs İçeriği</h2>
        </div>
        
        <div className="divide-y divide-gray-200">
          {lessons.length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              Henüz ders eklenmemiş
            </div>
          ) : (
            lessons.map((lesson, index) => (
              <div
                key={lesson.id}
                className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/learning/lesson/${lesson.id}`)}
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold">{index + 1}</span>
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {lesson.title}
                    </h3>
                    <p className="text-sm text-gray-600">{lesson.description}</p>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {lesson.duration && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock size={16} />
                        <span>{lesson.duration} dk</span>
                      </div>
                    )}
                    <button className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700">
                      <Play size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
